import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';
import { NewMessageAlertService } from '../message/new-message-alert.service';

const SERVICE_WORKER_URL = '/push-sw.js';

export type PushPermission = NotificationPermission | 'unsupported';

/** The browser side of web push: registers the service worker (push-sw.js), subscribes this device with the
 * server's key, and keeps the server's idea of "which user owns this device" right. Whatever a push says,
 * the in-app badge keeps working on its own (NewMessageAlertService) - push only makes it reach people whose
 * app is closed. */
@Injectable({
  providedIn: 'root'
})
export class PushService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly alerts = inject(NewMessageAlertService);
  private readonly apiUrl = `${environment.apiUrl}/push`;

  constructor() {
    // A device that allowed notifications while someone else was logged in belongs to whoever logs in now.
    this.authService.loggedIn$.subscribe(() => void this.resync());
    if (this.authService.isLoggedIn()) {
      void this.resync();
    }
    // The service worker tells open tabs about a push (refresh the badge) and about a tapped notification.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data?.type === 'cba-push') {
          this.alerts.checkSoon(true);
        }
      });
    }
  }

  get supported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  get permission(): PushPermission {
    return this.supported ? Notification.permission : 'unsupported';
  }

  /** iPhone/iPad only deliver web push to a page that was added to the home screen and opened from there. */
  get needsHomeScreenInstall(): boolean {
    const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
      || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    return ios && !standalone;
  }

  /** Does this device currently have a push subscription (that the server was told about at enable time)? */
  async isSubscribed(): Promise<boolean> {
    if (!this.supported) {
      return false;
    }
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
    return !!(registration && await registration.pushManager.getSubscription());
  }

  /** Asks for permission, subscribes this device and registers it with the server. Throws a readable Error if the
   * user declines or something is unsupported. */
  async enable(): Promise<void> {
    if (!this.supported) {
      throw new Error('Dieses Gerät oder dieser Browser unterstützt keine Push-Benachrichtigungen.');
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Benachrichtigungen wurden nicht erlaubt. Du kannst das in den Browser-/Geräte-Einstellungen ändern.');
    }
    const registration = await this.registerServiceWorker();
    const { publicKey } = await firstValueFrom(this.http.get<{ publicKey: string }>(`${this.apiUrl}/public-key`));
    let subscription = await registration.pushManager.getSubscription();
    if (subscription && !this.sameKey(subscription, publicKey)) {
      // created with another server key (e.g. the keys changed) - useless now
      await subscription.unsubscribe();
      subscription = null;
    }
    subscription ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.toUint8Array(publicKey)
    });
    await this.sendToServer(subscription);
  }

  /** Switches notifications off for this device (browser subscription and server entry). */
  async disable(): Promise<void> {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      return;
    }
    await this.removeFromServer(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }

  /** On logout: this device stops receiving the logged-out user's messages. The browser subscription stays, so
   * the next login (resync) simply claims it again. Never throws - logging out must not depend on it. */
  async detachDevice(): Promise<void> {
    try {
      if (!this.supported) {
        return;
      }
      const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await this.removeFromServer(subscription.endpoint);
      }
    } catch {
      // best effort
    }
  }

  /** Sends a test notification to all of the user's devices; resolves to how many it reached. */
  async sendTest(): Promise<number> {
    const response = await firstValueFrom(this.http.post<{ reached: number }>(`${this.apiUrl}/test`, {}));
    return response.reached;
  }

  /** Re-registers an existing subscription for the current user (idempotent) - only if notifications were
   * allowed before; never asks for anything. */
  private async resync(): Promise<void> {
    try {
      if (!this.supported || Notification.permission !== 'granted' || !this.authService.isLoggedIn()) {
        return;
      }
      const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await this.sendToServer(subscription);
      }
    } catch {
      // not important - the next page load tries again
    }
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
    return navigator.serviceWorker.ready;
  }

  private async sendToServer(subscription: PushSubscription): Promise<void> {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys['auth']) {
      throw new Error('Die Push-Anmeldung dieses Geräts ist unvollständig.');
    }
    await firstValueFrom(this.http.post<void>(`${this.apiUrl}/subscriptions`,
      { endpoint: json.endpoint, p256dh: json.keys['p256dh'], auth: json.keys['auth'] }));
  }

  private async removeFromServer(endpoint: string): Promise<void> {
    await firstValueFrom(this.http.delete<void>(`${this.apiUrl}/subscriptions`, { params: { endpoint } }));
  }

  private sameKey(subscription: PushSubscription, publicKey: string): boolean {
    const current = subscription.options.applicationServerKey;
    if (!current) {
      return false;
    }
    const expected = this.toUint8Array(publicKey);
    const actual = new Uint8Array(current);
    return expected.length === actual.length && expected.every((byte, index) => byte === actual[index]);
  }

  /** base64url (what the server sends) to the bytes PushManager.subscribe wants. */
  private toUint8Array(base64Url: string): Uint8Array {
    const padded = base64Url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
    const raw = atob(padded);
    return Uint8Array.from(raw, char => char.charCodeAt(0));
  }
}
